import React, { useMemo, useState, useEffect } from 'react'

import { Typography, TableFooter, TableRow, TableCell } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Table from 'components/Table'
import moment from 'moment'
import {
  TextField,
  Button,
  useQuery,
  Loading,
  Error,
  ListContextProvider,
  useTranslate,
} from 'react-admin'
import { post as getFromHasura } from 'utils/axios'

import {
  darkGray,
  black,
  lightergray3,
  orange,
  white,
} from '../../constants/colorConstants'
import ArrowDown from '../../images/arrowDown'
import ArrowUp from '../../images/arrowUp'
import {
  invoiceAdvanceTotalUsedAggregate,
  invoiceAdvanceTotalAllocatedAggregate,
} from '../../provider/queries/hasuraQueries'
import currencyFormatter from '../../utils/formatAmount'
import { DatagridWithFooter } from '../datagrids/datagridWithFooter'
import { EventCreditLineDatagrid } from '../datagrids/eventCreditLineDatagrid'
import { EventCreditLineTableEmpty } from '../emptyViews/eventCreditLineTableEmptyView'
import { CurrencyField } from '../fields/currencyField'
import { LocalDateField } from '../fields/localDateField'
import { ViewInvoiceField } from '../fields/viewInvoiceField'
import { Pagination } from './pagination'

const useStyles = makeStyles((theme) => ({
  gridCell: {
    fontSize: '13px',
    lineHeight: '15.23px',
    color: black,
    ...theme.mundi.font.roboto400Normal,
  },
  group: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: '14px',
    lineHeight: '16.41px',
    ...theme.mundi.font.roboto700Normal,
    color: darkGray,
  },
  field: {
    textAlign: 'right',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  button: {
    width: '24px',
    minWidth: 'auto',
    height: '20px',
    padding: '0px',
    marginLeft: '24px',
    fontSize: '14px',
    lineHeight: '16.41px',
    ...theme.mundi.font.roboto700Normal,
    color: darkGray,
  },
  buttonInvisible: {
    opacity: 0,
  },
  title: {
    paddingLeft: '20px',
    paddingTop: '20px',
    paddingBottom: '20px',
    fontSize: '14px',
    lineHeight: '16.41px',
    ...theme.mundi.font.roboto700Normal,
    color: darkGray,
  },
  grey: {
    backgroundColor: lightergray3,
  },
  redFooter: {
    backgroundColor: orange,
    fontSize: '16px',
    lineHeight: '18.75px',
    ...theme.mundi.font.roboto700Normal,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    height: '60px',
    paddingRight: '65px',
  },
  footerTextWhite: {
    fontSize: '14px',
    lineHeight: '16.41px',
    ...theme.mundi.font.roboto700Normal,
    color: white,
  },
  groupWhite: {
    marginLeft: '100px',
  },
  headerRightWithoutIconWhite: {
    backgroundColor: white,
    textAlign: 'right',
    paddingRight: '30px',
    background: 'transparent',
  },
  headerRightWithoutIconGrey: {
    backgroundColor: lightergray3,
    textAlign: 'right',
    paddingRight: '30px',
    background: 'transparent',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}))

export const DetailList = (props) => {
  const [page, setPage] = useState(1)
  const classes = useStyles()
  const translate = useTranslate()
  const [perPage, setPerPage] = useState(10)

  const { data, total, loading, error } = useQuery({
    type: 'getList',
    resource: 'invoices',
    payload: {
      where: {
        credit_line_id: { _eq: props.record.id },
        paid_at: { _is_null: false },
      },
      pagination: { page: page, perPage: perPage },
    },
  })

  if (loading) return <Loading />
  if (error) return <Error error={error} />
  if (!data) return null

  return (
    <div className={classes.grey}>
      <Typography className={classes.title} {...props}>
        {translate('resources.creditlines.messages.invoiceHistory').replace(
          '<<N>>',
          total
        )}
      </Typography>
      <ListContextProvider
        value={{
          data: data.reduce(
            (obj, item) => ({
              ...obj,
              [item.id]: item,
            }),
            {}
          ),
          ids: data.map(({ id }) => id),
          total,
          page,
          perPage,
          setPage,
          setPerPage,
          currentSort: { field: 'invoice', order: 'DESC' },
          selectedIds: [],
        }}
      >
        <EventCreditLineDatagrid />
        <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />
      </ListContextProvider>
    </div>
  )
}

export const EventCreditLineFooter = ({
  expanded,
  handleClick,
  invoicesCount,
  activeInvoicesCount,
  totalAllocated,
}) => {
  const classes = useStyles()
  const translate = useTranslate()

  return (
    <TableFooter>
      {activeInvoicesCount === 0 && (
        <TableRow>
          <TableCell colSpan={4}>
            <EventCreditLineTableEmpty />
          </TableCell>
        </TableRow>
      )}
      <TableRow>
        <TableCell colSpan={2} />
        <TableCell colSpan={2}>
          <div className={classes.group}>
            <Typography className={classes.footerText}>
              {translate('resources.creditlines.messages.currentlyAllocated')}{' '}
            </Typography>
            <Typography className={classes.footerText}>
              {currencyFormatter.format(totalAllocated)}
            </Typography>
            <Button
              disabled={!invoicesCount}
              className={
                invoicesCount ? classes.button : classes.buttonInvisible
              }
              onClick={handleClick}
            >
              {expanded ? <ArrowUp /> : <ArrowDown />}
            </Button>
          </div>
        </TableCell>
      </TableRow>
    </TableFooter>
  )
}

export const EventCreditLineList = ({ linkDataInvoices, ...props }) => {
  const classes = useStyles()
  const translate = useTranslate()
  const [expanded, setExpanded] = useState(false)

  const handleClick = () => {
    setExpanded(!expanded)
  }

  return (
    <DatagridWithFooter
      {...props}
      footer={
        <EventCreditLineFooter
          expanded={expanded}
          handleClick={handleClick}
          record={props.footerData}
        />
      }
      empty={!props.record.activeInvoicesCount && <EventCreditLineTableEmpty />}
    >
      <TextField source="id" />
      <ViewInvoiceField
        className={classes.gridCell}
        sourceInvoiceNumber="invoiceNumber"
        sourceInvoiceId="id"
        linkDataInvoices={linkDataInvoices}
        label={'resources.creditlines.events.invoice'}
        mode="selectInvoice"
      />
      <LocalDateField
        className={classes.gridCell}
        source="issuedDate"
        label={'resources.creditlines.events.issueDate'}
      />
      <LocalDateField
        className={classes.gridCell}
        source="dueDate"
        label={'resources.creditlines.events.dueDate'}
      />
      <CurrencyField
        className={classes.gridCell}
        source="inUsed"
        label={'resources.creditlines.events.inUsed'}
        headerClassName={classes.headerRightWithoutIconWhite}
      />
      <TextField />
      {expanded && (
        <>
          <DetailList
            linkDataInvoices={linkDataInvoices}
            record={props.record}
          />
          <span className={classes.redFooter}>
            <Typography className={classes.footerTextWhite}>
              {translate('resources.creditlines.messages.totalUsed')}
            </Typography>
            <span className={classes.groupWhite}>
              <CurrencyField
                record={props.record}
                className={classes.footerTextWhite}
                source="totalAllocated"
                label={'resources.creditlines.events.inUsed'}
                headerClassName={classes.headerRightWithoutIconWhite}
              />
            </span>
          </span>
        </>
      )}
    </DatagridWithFooter>
  )
}

export const SimpleCreditLineList = ({ items, invoiceCount, ...props }) => {
  const classes = useStyles()
  const t = useTranslate()
  const [expanded, setExpanded] = useState(false)
  const [totalUsed, setTotalUsed] = useState(0)
  const [totalAllocated, setTotalAllocated] = useState(0)

  const dateFormat = (value) => moment(value).format('DD/MM/yyyy')
  const columns = useMemo(
    () => [
      {
        key: 'invoiceNumber',
        label: t('resources.creditlines.events.invoice'),
      },
      {
        key: 'issuedDate',
        label: t('resources.creditlines.events.issueDate'),
        applyFormat: dateFormat,
      },
      {
        key: 'dueDate',
        label: t('resources.creditlines.events.dueDate'),
        applyFormat: dateFormat,
      },
      {
        key: 'allocated',
        label: t('resources.creditlines.events.inUsed'),
        applyFormat: currencyFormatter.format,
      },
    ],
    [t]
  )

  useEffect(() => {
    ;(async function () {
      let total = 0
      const result = await getFromHasura({
        payload: invoiceAdvanceTotalAllocatedAggregate,
        variables: { creditLineId: props.record.id },
      })

      if (result.data) {
        const { sum } =
          result.data?.invoiceadvance_view_invoice_advance_summary_aggregate
            ?.aggregate

        total = sum['allocated']
      }

      return setTotalAllocated(total)
    })()
  }, [props.record.id])

  useEffect(() => {
    ;(async function () {
      if (!expanded) return

      let total = 0
      const result = await getFromHasura({
        payload: invoiceAdvanceTotalUsedAggregate,
        variables: {
          creditLineId: props.record.id,
        },
      })

      if (result.data) {
        const { sum } =
          result.data?.invoiceadvance_view_invoice_advance_summary_aggregate
            ?.aggregate

        total = sum['repaid_amount'] + sum['allocated']
      }

      return setTotalUsed(total)
    })()
  }, [expanded, props.record.id])

  return (
    <>
      <Table
        columns={columns}
        rows={items}
        footer={
          <EventCreditLineFooter
            {...props}
            invoicesCount={invoiceCount - items.length || 0}
            activeInvoicesCount={items.length || 0}
            totalAllocated={totalAllocated}
            expanded={expanded}
            handleClick={() => setExpanded((prevState) => !prevState)}
          />
        }
      />
      {expanded && (
        <>
          <DetailList linkDataInvoices={items} record={props.record} />
          <span className={classes.redFooter}>
            <Typography className={classes.footerTextWhite}>
              {t('resources.creditlines.messages.totalUsed')}
            </Typography>
            <span className={classes.groupWhite}>
              <Typography className={classes.footerTextWhite}>
                {currencyFormatter.format(totalUsed)}
              </Typography>
            </span>
          </span>
        </>
      )}
    </>
  )
}
